import * as React from "react"

import { Badge, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useGlobalBranchRows } from "@/features/finance/overview/lib/use-global-branch-rows"

/**
 * Branches that recorded NO money this period — neither in nor out.
 *
 * The one thing a consolidated total cannot show you. A branch that stops posting looks identical to a branch
 * with nothing to report, and both vanish into a workspace total that still looks healthy because the other
 * branches carry it. Silence is the earliest signal that a book is not being kept, and it is the only widget
 * here whose EMPTY state is the good news.
 *
 * GLOBAL only: at branch scope the question is meaningless (you are the branch, and you know).
 */
export function SilentBranchesWidget() {
  const t = useTranslations("finance.overview")
  const { rows, isLoading, isError, isFetching, refetch } = useGlobalBranchRows()

  const data = React.useMemo(() => {
    const silent = rows.filter((row) => row.isSilent)
    return { silent, total: rows.length, reporting: rows.length - silent.length }
  }, [rows])

  useWidgetMenu({ refresh: refetch })

  return (
    <WidgetBoundary
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      data={data}
      // Never "empty": every branch reporting is a RESULT, and blanking the widget would hide it.
      skeleton={<SilentSkeleton />}
      errorTitle={t("widgets.silentBranches.error")}
      onRetry={refetch}
      retryLabel={t("widgets.common.retry")}
    >
      {(d) =>
        d.silent.length === 0 ? (
          <div className='space-y-1'>
            <p className='text-foreground text-2xl font-semibold tabular-nums tracking-tight'>{d.total}</p>
            <p className='text-muted-foreground text-xs'>{t("widgets.silentBranches.allReporting")}</p>
          </div>
        ) : (
          <div className='space-y-3'>
            <div className='flex items-end justify-between gap-2'>
              <p className='text-foreground text-2xl font-semibold tabular-nums tracking-tight'>{d.silent.length}</p>
              <p className='text-muted-foreground text-xs'>
                {t("widgets.silentBranches.ratio", { silent: d.silent.length, total: d.total })}
              </p>
            </div>
            <ul className='divide-border/60 divide-y'>
              {d.silent.map((row) => (
                <li key={row.id} className='flex items-center justify-between gap-2 py-1.5'>
                  <span className='text-foreground min-w-0 truncate text-sm'>{row.name}</span>
                  <Badge variant='secondary' className='shrink-0'>
                    {t("widgets.silentBranches.noActivity")}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )
      }
    </WidgetBoundary>
  )
}

function SilentSkeleton() {
  return (
    <div className='space-y-3'>
      <Skeleton className='h-7 w-12' />
      {[0, 1].map((row) => (
        <div key={row} className='flex items-center justify-between py-1'>
          <Skeleton className='h-3 w-28' />
          <Skeleton className='h-4 w-16 rounded-full' />
        </div>
      ))}
    </div>
  )
}
